/* eslint-disable react/require-default-props */

import React from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import {
  params, string, arrayOf, shape, extend
} from '@thd-nucleus/data-sources';
import { FourApplianceTemplate } from '@thd-olt-component-react/dynamic-image-grid';
import { MediaGalleryDesktop, MediaGalleryMobile, MediaGalleryCarousel } from '@thd-olt-component-react/media-gallery';
import {
  getBundleMedia,
  getDynamicImages
} from '../utils/product-bundle-utils';
import useDeviceType from '../hooks/useDeviceType';
import { getFilterResponseFlag } from '../utils/product-unavailable-utils';

const { arrayOf: arrayOfProp, bool: boolProp, shape: shapeProp, func: funcProp } = proptypes;

/**
 * Props
 *
 * @typedef {object} Props
 * @property {object} product The main product
 * @property {array} products An array of products in a bundle
 * @property {bool} showLoader Show loader flag
 */

/**
 * Returns a media gallery including images, video and three-sixty
 * @param {Props} Props
 * @returns {React.ReactElement} JSX
 */

const BundleMediaGallery = ({
  product = null,
  products = null,
  showLoader = false,
  addProduct
}) => {
  const fsFilmstrip = useConfigService('fs:isFilmstripEnable');
  const { isMobile } = useDeviceType();
  let bundleMedia = null;
  const { dynamicImageGrid } = product?.features ?? {};
  const filterResponse = getFilterResponseFlag(product);
  if (product && products) {
    bundleMedia = getBundleMedia(product, products);
  }

  if (dynamicImageGrid) {
    return (
      <FourApplianceTemplate
        customStyles="sui-h-full sui-mt-4"
        images={getDynamicImages(products)}
        handlePlaceholderClick={(swappedItem, itemId) => {
          if (swappedItem) {
            addProduct(itemId, swappedItem?.product?.itemId);
          }
        }}
        filterResponse={filterResponse}
      />
    );
  }

  const getMobileComponent = (bundleMediaData, showLoaderFlag) => {
    if (fsFilmstrip) {
      return (
        <div data-component="BundleMediaGalleryCarousel" data-testid="bundle-media-gallery-carousel">
          <MediaGalleryCarousel
            showLoader={showLoaderFlag}
            overrideData={bundleMediaData}
            mainImageFetchPriority="high"
            overlayType="filmstrip"
            itemId={product?.itemId}
          />
        </div>
      );
    }
    return (
      <div data-component="BundleMediaGalleryMobile" data-testid="bundle-media-gallery-mobile">
        <MediaGalleryMobile
          showLoader={showLoaderFlag}
          overrideData={bundleMediaData}
          itemId={product?.itemId}
        />
      </div>
    );
  };

  return (
    <div data-component="BundleMediaGallery" data-testid="bundle-media-gallery">
      {isMobile ? (
        getMobileComponent(bundleMedia, showLoader)
      ) : (
        <div data-component="BundleMediaGalleryDesktop" data-testid="bundle-media-gallery-desktop">
          <MediaGalleryDesktop
            showLoader={showLoader}
            overrideData={bundleMedia}
            mainImageFetchPriority="high"
            itemId={product?.itemId}
          />
        </div>
      )}
    </div>
  );
};

BundleMediaGallery.propTypes = {
  showLoader: boolProp,
  product: shapeProp({
    media: shapeProp()
  }),
  products: arrayOfProp(shapeProp()),
  addProduct: funcProp.isRequired
};

BundleMediaGallery.displayName = 'BundleMediaGallery';

BundleMediaGallery.dataModel = extend(
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      media: shape({
        images: arrayOf({
          sizes: arrayOf(string()),
          subType: string(),
          type: string(),
          url: string()
        }),
        threeSixty: arrayOf({
          id: string(),
          url: string()
        }),
        video: arrayOf({
          shortDescription: string(),
          title: string(),
          thumbnail: string(),
          type: string(),
          url: string(),
          videoId: string(),
          videoStill: string()
        })
      }),
      identifiers: shape({
        productLabel: string()
      })
    })
  },
  MediaGalleryDesktop,
  MediaGalleryMobile
);

export { BundleMediaGallery };
